import { TaggedValue } from '../core';

import {
  TaggedEnum,
  TaggedEnumExtension,
  TaggedEnumTypeDefinition,
  TaggedEnumTypeDescriptor,
} from './core';

export function tagEnum<Tag extends string, Value extends number = number>(
  _tag: Tag,
  v: Value,
) {
  return v as TaggedEnum<Tag, Value>;
}

export function extendEnum<
  Tag extends string,
  Base extends TaggedValue<string, number>,
>(_tag: Tag, v: Base) {
  return v as TaggedEnumExtension<Tag, Base>;
}

export function buildTaggedEnumType<
  Tag extends string,
  Value extends number,
  T extends object,
>(
  $: Tag,
  enumObj: T,
): Prettify<TransformToTaggedEnumDict<Tag, FilterEnumValues<T, Value>>> {
  return Object.keys(enumObj).reduce(
    (o, key) => ({
      ...o,
      [key]: tagEnum($, (enumObj as Record<string, Value>)[key]!),
    }),
    {} as TransformToTaggedEnumDict<Tag, FilterEnumValues<T, Value>>,
  );
}

export function describeTaggedEnumType<
  Tag extends string,
  Value extends TaggedEnum<Tag>,
  Type extends TaggedEnumTypeDefinition<Tag, Value>,
>($: Tag, enumObj: Type): TaggedEnumTypeDescriptor<Value> {
  return Object.keys(enumObj).reduce((o, key) => {
    const label = key;
    const slug = label.toLowerCase();
    const value = enumObj[key]!;
    return { ...o, [value]: { value, slug, label } };
  }, {} as TaggedEnumTypeDescriptor<Value>);
}

type FilterEnumValues<EnumObj, V extends number> = RemoveIndexSignature<{
  [K in keyof EnumObj]: K extends string
    ? EnumObj[K] extends V
      ? EnumObj[K]
      : number
    : never;
}>;

type Prettify<T> = {
  [K in keyof T]: T[K];
} & {};

type RemoveIndexSignature<T> = {
  [K in keyof T as string extends K
    ? never
    : number extends K
      ? never
      : symbol extends K
        ? never
        : K]: T[K];
};

type TransformToTaggedEnumDict<
  Tag extends string,
  T extends Record<string, number>,
> = {
  [K in keyof T]: TaggedEnum<Tag, T[K]>;
};
